import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Network, ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCcw, Loader2 } from "lucide-react";
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { useQuery } from "@tanstack/react-query";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { ContentComingSoon } from "@/components/viewers/ContentComingSoon";
import { MindMapTree, type MindMapNode } from "@/components/viewers/MindMapTree";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

export function MindMapPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Mind Map");
  const { reportEndReached } = useViewerTracker({
    chapterId,
    mode: "mind-map",
    active: paywall === null,
    minDwellSeconds: 60,
  });

  const { url, contentType, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "mind-map",
    enabled: shouldFetch,
  });

  // Content Factory mind maps are interactive JSON trees; legacy maps are
  // static PNGs. Decide from the asset content_type, falling back to the
  // signed-URL file extension. Unknown -> treat as legacy image.
  const isJsonTree = useMemo(() => detectJsonTree(contentType, url), [contentType, url]);
  const tree = useMindMapJson(url, isJsonTree);

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;
  const chapterName = chapter?.chapter_name ?? "mindmap";

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Mind Map"
      modeIcon={Network}
      modeAccent="bg-teal-500/10 text-teal-600 dark:text-teal-400"
    >
      {paywall ?? (
        isLocked ? (
          <PaywallCard modeLabel="Mind map" classId={classId} />
        ) : isError ? (
          <GenericError onRetry={refresh} />
        ) : isLoading ? (
          <Loading />
        ) : isNotFound || !url ? (
          <ComingSoon chapterId={chapterId} chapterName={chapter?.chapter_name} />
        ) : isJsonTree ? (
          tree.isLoading ? (
            <Loading />
          ) : tree.isError || !tree.data ? (
            <ComingSoon chapterId={chapterId} chapterName={chapter?.chapter_name} />
          ) : (
            <MindMapTree data={tree.data} onFullyExplored={reportEndReached} />
          )
        ) : (
          <MindMapCanvas url={url} chapterName={chapterName} onLoaded={reportEndReached} />
        )
      )}
    </ViewerScaffold>
  );
}

// --------------------------------------------------------------------------
// Format detection + JSON loading for Content Factory mind maps.
// --------------------------------------------------------------------------
function detectJsonTree(contentType: string | null, url: string | null): boolean {
  if (contentType) {
    if (contentType.includes("json")) return true;
    if (contentType.startsWith("image/")) return false;
  }
  if (url) {
    const path = url.split("?")[0].toLowerCase();
    if (path.endsWith(".json")) return true;
    if (
      path.endsWith(".png") ||
      path.endsWith(".jpg") ||
      path.endsWith(".jpeg") ||
      path.endsWith(".webp")
    ) {
      return false;
    }
  }
  return false; // unknown -> legacy image
}

function useMindMapJson(url: string | null, enabled: boolean) {
  const query = useQuery({
    queryKey: ["mindmap-json", url ?? null],
    enabled: enabled && !!url,
    staleTime: 60 * 60 * 1000,
    retry: 0,
    queryFn: async (): Promise<MindMapNode | null> => {
      const res = await fetch(url!);
      if (!res.ok) throw new Error(`Mind map fetch failed: ${res.status}`);
      return normalizeMindMap(await res.json());
    },
  });
  return {
    data: query.data ?? null,
    isLoading: enabled && !!url && query.isLoading,
    isError: query.isError,
  };
}

/**
 * Coerce assorted tree shapes into `{ name, children }`. Tolerates
 * `title`/`label` aliases and a `nodes` child key, and accepts a top-level
 * array (wrapped under a synthetic root). Prunes empty leaves.
 */
function normalizeMindMap(raw: unknown): MindMapNode | null {
  const norm = (node: unknown): MindMapNode | null => {
    if (!node || typeof node !== "object") return null;
    const o = node as Record<string, unknown>;
    const name =
      typeof o.name === "string"
        ? o.name
        : typeof o.title === "string"
          ? o.title
          : typeof o.label === "string"
            ? o.label
            : "";
    // `note` is no longer emitted (detail is now ordinary leaf nodes); ignore
    // it if an old cached map still carries one.
    const rawChildren = Array.isArray(o.children)
      ? o.children
      : Array.isArray(o.nodes)
        ? o.nodes
        : [];
    const children = rawChildren.map(norm).filter((c): c is MindMapNode => c !== null);
    if (name === "" && children.length === 0) return null;
    return children.length ? { name, children } : { name };
  };
  if (Array.isArray(raw)) {
    const children = raw.map(norm).filter((c): c is MindMapNode => c !== null);
    return children.length ? { name: "Mind Map", children } : null;
  }
  return norm(raw);
}

function Loading() {
  return (
    <div className="flex-1 grid place-items-center text-sm text-text-muted">
      Loading mind map…
    </div>
  );
}

// --------------------------------------------------------------------------
function MindMapCanvas({
  url,
  chapterName,
  onLoaded,
}: {
  url: string;
  chapterName: string;
  /** A legacy PNG map has no branches to open, so "rendered" is the only end
   *  signal available. The tracker's dwell floor is what makes it mean anything. */
  onLoaded: () => void;
}) {
  const ref = useRef<ReactZoomPanPinchRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      setFullscreen(false);
    } else {
      void el.requestFullscreen();
      setFullscreen(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        // Definite height + overflow-hidden so the zoom/pan surface is locked
        // to the viewport instead of growing and scrolling the page.
        "relative w-full overflow-hidden bg-surface-2 dark:bg-bg",
        fullscreen ? "h-screen" : "h-[calc(100vh-9rem)]",
      )}
    >
      <TransformWrapper
        ref={ref}
        initialScale={1}
        minScale={0.4}
        maxScale={6}
        doubleClick={{ mode: "toggle", step: 1.4 }}
        wheel={{ step: 0.1, smoothStep: 0.0035 }}
        pinch={{ step: 5 }}
        panning={{ velocityDisabled: false }}
        limitToBounds={false}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            <TransformComponent
              wrapperClass="!w-full !h-full"
              contentClass="!w-full !h-full grid place-items-center"
            >
              <img
                src={url}
                alt={`${chapterName} mind map`}
                onLoad={() => { setImgLoaded(true); onLoaded(); }}
                draggable={false}
                className={cn(
                  "max-w-none transition-opacity duration-200",
                  imgLoaded ? "opacity-100" : "opacity-0",
                  "select-none",
                )}
                style={{ touchAction: "none" }}
              />
            </TransformComponent>

            {!imgLoaded ? (
              <div className="absolute inset-0 grid place-items-center text-sm text-text-muted">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-brand" />
                  Loading image…
                </div>
              </div>
            ) : null}

            {/* Toolbar pill -- top-right on desktop, top-centre on mobile.
                Stays out of the safe area; large touch targets. */}
            <div
              className={cn(
                "absolute z-10 flex items-center gap-1 p-1 rounded-full",
                "border border-border bg-surface/95 backdrop-blur-md shadow-md",
                "top-3 right-3 sm:top-4 sm:right-4",
              )}
            >
              <Button
                variant="ghost"
                size="icon"
                aria-label="Zoom out"
                onClick={() => zoomOut()}
                className="h-9 w-9 rounded-full"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Reset zoom"
                onClick={() => resetTransform()}
                className="h-9 w-9 rounded-full"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Zoom in"
                onClick={() => zoomIn()}
                className="h-9 w-9 rounded-full"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <span className="w-px h-5 bg-border mx-0.5" />
              <Button
                variant="ghost"
                size="icon"
                aria-label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                onClick={toggleFullscreen}
                className="h-9 w-9 rounded-full"
              >
                {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>

            <p className="absolute z-10 bottom-3 left-1/2 -translate-x-1/2 text-[11px] sm:text-xs text-text-subtle bg-surface/90 backdrop-blur px-3 py-1 rounded-full border border-border">
              <span className="sm:hidden">Pinch to zoom • drag to pan</span>
              <span className="hidden sm:inline">Scroll to zoom • drag to pan • double-click to step</span>
            </p>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}

function ComingSoon({ chapterId, chapterName }: { chapterId?: string; chapterName?: string | null }) {
  return (
    <ContentComingSoon
      chapterId={chapterId}
      chapterName={chapterName}
      title="The mind map for this chapter is almost ready"
      modeIcon={Network}
      modeAccent="bg-teal-500/10 text-teal-600 dark:text-teal-400"
    />
  );
}
